/* eslint-disable react/no-unescaped-entities */
/**
 * Super Admin — AI Governance
 *
 * Three areas:
 *   1) Global policy   — tenant_choice | force_managed | force_byo + standby/forced model
 *   2) Per-tenant view — every tenant's effective provider + 30-day usage + override button
 *   3) Audit log       — per-call rows
 */
import { useEffect, useState } from "react";
import { Save, Brain, ShieldAlert, Eye, Pin, Trash2, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const MODES = [
    { value: "tenant_choice", label: "Tenant choice", sub: "Firms pick their own provider; managed is standby" },
    { value: "force_managed", label: "Force managed", sub: "All firms use the Emergent platform key (wallet debit)" },
    { value: "force_byo", label: "Force BYO", sub: "Firms MUST upload their own key — no managed fallback" },
];

const PROVIDERS = ["openai", "anthropic", "gemini"];

export default function AdminAIGovernance() {
    const [policy, setPolicy] = useState(null);
    const [overview, setOverview] = useState([]);
    const [audit, setAudit] = useState([]);
    const [tiers, setTiers] = useState(null);
    const [balances, setBalances] = useState([]);
    const [days, setDays] = useState(30);
    const [saving, setSaving] = useState(false);
    const [overrideOpen, setOverrideOpen] = useState(null);
    const [tier1Open, setTier1Open] = useState(false);

    const load = async () => {
        try {
            const [p, o, a, t, b] = await Promise.all([
                api.get("/platform/ai-policy"),
                api.get(`/platform/ai-overview?days=${days}`),
                api.get("/platform/ai-usage-audit?limit=50"),
                api.get("/platform/ai-tiers"),
                api.get("/platform/ai-provider-balances"),
            ]);
            setPolicy(p.data);
            setOverview(o.data.tenants || []);
            setAudit(a.data || []);
            setTiers(t.data);
            setBalances(b.data || []);
        } catch (e) { toast.error(apiError(e)); }
    };

    useEffect(() => { load(); }, [days]);

    const savePolicy = async () => {
        if (!policy) return;
        setSaving(true);
        try {
            await api.patch("/platform/ai-policy", policy);
            toast.success("AI policy saved");
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    const toggleTierGlobal = async (tier, enabled) => {
        try {
            await api.post("/platform/ai-scope-rules", { tier, scope_type: "global", enabled });
            toast.success(`Tier ${tier} ${enabled ? "enabled" : "disabled"} globally`);
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const [tenantsQ, setTenantsQ] = useState("");
    const [auditQ, setAuditQ] = useState("");
    const filteredOverview = overview.filter(r => {
        if (!tenantsQ) return true;
        const q = tenantsQ.toLowerCase();
        return [r.tenant_name, r.effective_provider, r.effective_model, r.effective_source].some(v => (v || "").toLowerCase().includes(q));
    });
    const filteredAudit = audit.filter(r => {
        if (!auditQ) return true;
        const q = auditQ.toLowerCase();
        return [r.tenant_name, r.user_name, r.kind, r.provider, r.model, r.source].some(v => (v || "").toLowerCase().includes(q));
    });

    const balancesCtrl = useTableControls(balances, {
        accessors: { tier_label: (b) => `T${b.tier} ${b.label}` },
    });
    const overviewCtrl = useTableControls(filteredOverview, {
        accessors: {
            effective: (r) => `${r.effective_provider} ${r.effective_model}`,
            last_used: (r) => r.last_used_at,
        },
    });
    const auditCtrl = useTableControls(filteredAudit, {
        accessors: {
            when: (r) => r.created_at,
            provider_model: (r) => `${r.provider} ${r.model}`,
            inout: (r) => Number(r.input_chars || 0) + Number(r.output_chars || 0),
        },
    });

    if (!policy) {
        return <div className="text-center text-muted-foreground text-sm py-12"><Loader2 className="w-4 h-4 inline animate-spin mr-2"/>Loading…</div>;
    }

    return (
        <div className="space-y-8" data-testid="admin-ai-governance">
            <div>
                <div className="overline">Super Admin</div>
                <h1 className="display-display mt-1 inline-flex items-center gap-3">
                    <Brain className="w-7 h-7 text-primary" strokeWidth={1.5}/> AI Governance
                </h1>
                <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                    Control which AI providers tenants may use, pin specific models on individual firms, and audit every
                    AI call across the platform. Primary keys are BYO (firm-owned); the Emergent Universal Key is the
                    managed standby.
                </p>
            </div>

            {/* ============ POLICY ============ */}
            <section className="bg-card border border-border p-6" data-testid="ai-policy-card">
                <div className="flex items-center gap-2 mb-4">
                    <ShieldAlert className="w-4 h-4 text-primary"/>
                    <h2 className="text-sm uppercase tracking-[0.15em] text-foreground">Global policy</h2>
                </div>
                <div>
                    <label className="overline">Mode</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                        {MODES.map(m => (
                            <button
                                key={m.value}
                                onClick={() => setPolicy({ ...policy, mode: m.value })}
                                data-testid={`policy-mode-${m.value}`}
                                className={`text-left px-3 py-2 border ${policy.mode === m.value ? "border-primary bg-primary/10" : "border-border hover:border-muted"}`}
                            >
                                <div className="text-sm text-foreground">{m.label}</div>
                                <div className="text-[10px] text-muted-foreground mt-0.5">{m.sub}</div>
                            </button>
                        ))}
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                    <Field label="Standby provider (managed)" testid="standby-provider">
                        <Select value={policy.standby_provider} onChange={v => setPolicy({ ...policy, standby_provider: v })} options={PROVIDERS}/>
                    </Field>
                    <Field label="Standby model" testid="standby-model">
                        <Input value={policy.standby_model} onChange={v => setPolicy({ ...policy, standby_model: v })} placeholder="gpt-4o-mini"/>
                    </Field>
                    <Field label="Forced provider (mode = force_managed)" testid="forced-provider">
                        <Select value={policy.forced_provider} onChange={v => setPolicy({ ...policy, forced_provider: v })} options={PROVIDERS}/>
                    </Field>
                    <Field label="Forced model" testid="forced-model">
                        <Input value={policy.forced_model} onChange={v => setPolicy({ ...policy, forced_model: v })} placeholder="gpt-4o-mini"/>
                    </Field>
                </div>
                <div className="mt-5 flex items-center justify-between">
                    <div className="text-[11px] text-muted-foreground">
                        Allowed providers: <span className="text-foreground">{(policy.allowed_providers || []).join(", ")}</span>
                    </div>
                    <button onClick={savePolicy} disabled={saving} data-testid="policy-save" className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-[#C9A537] disabled:opacity-40 text-xs font-medium uppercase tracking-[0.15em]">
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Save className="w-3.5 h-3.5"/>} Save policy
                    </button>
                </div>
            </section>

            {/* ============ TIERS ============ */}
            {tiers && (
                <section className="bg-card border border-border p-6" data-testid="ai-tiers-card">
                    <div className="flex items-center gap-2 mb-4">
                        <Pin className="w-4 h-4 text-primary"/>
                        <h2 className="text-sm uppercase tracking-[0.15em] text-foreground">Provider tiers (failover order)</h2>
                    </div>
                    <p className="text-[11px] text-muted-foreground mb-4 max-w-3xl">
                        Calls cascade through tiers when one is unavailable or disabled. Tier 1 + 2 are billable to the
                        tenant wallet with the configured margin; Tier 3 (BYO) is free to the platform.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <TierCard tier={tiers.tier1} num={1} onConfigure={() => setTier1Open(true)} onToggleGlobal={toggleTierGlobal}/>
                        <TierCard tier={tiers.tier2} num={2} onToggleGlobal={toggleTierGlobal}/>
                        <TierCard tier={tiers.tier3} num={3} onToggleGlobal={toggleTierGlobal}/>
                    </div>
                </section>
            )}

            {/* ============ PROVIDER BALANCES ============ */}
            {balances.length > 0 && (
                <section className="bg-card border border-border" data-testid="ai-balances-card">
                    <header className="px-6 py-4 border-b border-border">
                        <h2 className="text-sm uppercase tracking-[0.15em] text-foreground">Provider balances</h2>
                        <p className="text-[11px] text-muted-foreground mt-1">Loaded amount (what you topped-up at the provider) − Utilized (our audit) = remaining balance. Profit = billed-to-tenants − raw cost.</p>
                    </header>
                    <div className="overflow-x-auto">
                        <FilterChip ctrl={balancesCtrl} className="m-3"/>
                        <table className="w-full text-xs">
                            <thead className="bg-background text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                                <tr>
                                    <SortableTh field="tier_label" ctrl={balancesCtrl} className="text-left px-4 py-2">Tier</SortableTh>
                                    <SortableTh field="provider" ctrl={balancesCtrl} className="text-left px-4 py-2">Provider</SortableTh>
                                    <SortableTh field="loaded_amount" ctrl={balancesCtrl} className="text-right px-4 py-2" align="right">Loaded (₹)</SortableTh>
                                    <SortableTh field="utilized_inr" ctrl={balancesCtrl} className="text-right px-4 py-2" align="right">Utilized (₹)</SortableTh>
                                    <SortableTh field="balance_inr" ctrl={balancesCtrl} className="text-right px-4 py-2" align="right">Balance (₹)</SortableTh>
                                    <SortableTh field="billed_to_tenants_inr" ctrl={balancesCtrl} className="text-right px-4 py-2" align="right">Billed (₹)</SortableTh>
                                    <SortableTh field="profit_inr" ctrl={balancesCtrl} className="text-right px-4 py-2" align="right">Profit (₹)</SortableTh>
                                    <SortableTh field="calls" ctrl={balancesCtrl} className="text-right px-4 py-2" align="right">Calls</SortableTh>
                                </tr>
                            </thead>
                            <tbody>
                                {balancesCtrl.rows.map(b => (
                                    <tr key={b.tier} className="border-t border-border" data-testid={`balance-row-tier-${b.tier}`}>
                                        <td className="px-4 py-2 text-foreground">T{b.tier} · {b.label}</td>
                                        <td className="px-4 py-2 text-muted-foreground">{b.provider}</td>
                                        <td className="px-4 py-2 text-right text-foreground">{b.loaded_amount === null ? "—" : b.loaded_amount.toFixed(2)}</td>
                                        <td className="px-4 py-2 text-right text-foreground">{b.utilized_inr.toFixed(2)}</td>
                                        <td className="px-4 py-2 text-right text-foreground">{b.balance_inr === null ? "—" : b.balance_inr.toFixed(2)}</td>
                                        <td className="px-4 py-2 text-right text-foreground">{(b.billed_to_tenants_inr || 0).toFixed(2)}</td>
                                        <td className="px-4 py-2 text-right text-emerald-400">{(b.profit_inr || 0).toFixed(2)}</td>
                                        <td className="px-4 py-2 text-right text-muted-foreground">{b.calls}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* ============ TENANTS ============ */}
            <section className="bg-card border border-border" data-testid="ai-tenants-card">
                <header className="px-6 py-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-primary"/>
                        <h2 className="text-sm uppercase tracking-[0.15em] text-foreground">Per-tenant visibility</h2>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
                            <input
                                value={tenantsQ}
                                onChange={e => setTenantsQ(e.target.value)}
                                placeholder="Search tenant / provider / model…"
                                className="bg-background border border-border focus:border-primary outline-none pl-9 pr-3 py-1.5 text-xs w-72"
                                data-testid="tenants-search-input"
                            />
                        </div>
                        <select value={days} onChange={e => setDays(parseInt(e.target.value))} className="bg-background border border-border px-2 py-1 text-xs text-foreground">
                            <option value={7}>Last 7 days</option>
                            <option value={30}>Last 30 days</option>
                            <option value={90}>Last 90 days</option>
                        </select>
                    </div>
                </header>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-background text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                            <tr>
                                <th className="text-left px-4 py-2">Tenant</th>
                                <th className="text-left px-4 py-2">Effective</th>
                                <th className="text-left px-4 py-2">Source</th>
                                <th className="text-right px-4 py-2">Calls</th>
                                <th className="text-right px-4 py-2">Cost (₹)</th>
                                <th className="text-left px-4 py-2">Last used</th>
                                <th className="px-4 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredOverview.map(r => (
                                <tr key={r.tenant_id} className="border-t border-border" data-testid={`tenant-row-${r.tenant_id}`}>
                                    <td className="px-4 py-3 text-foreground">{r.tenant_name}</td>
                                    <td className="px-4 py-3 text-foreground">{r.effective_provider} · {r.effective_model}</td>
                                    <td className="px-4 py-3"><SourceBadge source={r.effective_source}/></td>
                                    <td className="px-4 py-3 text-right text-foreground">{r.calls_period}</td>
                                    <td className="px-4 py-3 text-right text-foreground">{r.cost_inr_period.toFixed(2)}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{r.last_used_at ? new Date(r.last_used_at).toLocaleString() : "—"}</td>
                                    <td className="px-4 py-3">
                                        <button onClick={() => setOverrideOpen(r)} data-testid={`override-${r.tenant_id}`} className="text-[10px] uppercase tracking-[0.15em] text-primary hover:underline inline-flex items-center gap-1">
                                            <Pin className="w-3 h-3"/> {r.has_super_override ? "Edit pin" : "Pin"}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {overviewCtrl.rows.length === 0 && (
                                <tr><td colSpan={7} className="text-center text-muted-foreground py-8">{tenantsQ ? "No matches" : "No tenants"}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* ============ AUDIT LOG ============ */}
            <section className="bg-card border border-border" data-testid="ai-audit-card">
                <header className="px-6 py-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="text-sm uppercase tracking-[0.15em] text-foreground">Recent AI calls (per-call audit)</h2>
                        <p className="text-[11px] text-muted-foreground mt-1">Latest 50 across the platform.</p>
                    </div>
                    <div className="relative">
                        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
                        <input
                            value={auditQ}
                            onChange={e => setAuditQ(e.target.value)}
                            placeholder="Search tenant / user / kind / model…"
                            className="bg-background border border-border focus:border-primary outline-none pl-9 pr-3 py-1.5 text-xs w-72"
                            data-testid="audit-search-input"
                        />
                    </div>
                </header>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-background text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                            <tr>
                                <th className="text-left px-4 py-2">When</th>
                                <th className="text-left px-4 py-2">Tenant</th>
                                <th className="text-left px-4 py-2">User</th>
                                <th className="text-left px-4 py-2">Kind</th>
                                <th className="text-left px-4 py-2">Provider · Model</th>
                                <th className="text-left px-4 py-2">Source</th>
                                <th className="text-right px-4 py-2">In/Out chars</th>
                                <th className="text-right px-4 py-2">₹</th>
                            </tr>
                        </thead>
                        <tbody data-testid="ai-audit-rows">
                            {filteredAudit.map(r => (
                                <tr key={r.id} className="border-t border-border">
                                    <td className="px-4 py-2 text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
                                    <td className="px-4 py-2 text-foreground">{r.tenant_name}</td>
                                    <td className="px-4 py-2 text-muted-foreground">{r.user_name}</td>
                                    <td className="px-4 py-2 text-foreground">{r.kind}</td>
                                    <td className="px-4 py-2 text-foreground">{r.provider} · {r.model}</td>
                                    <td className="px-4 py-2"><SourceBadge source={r.source}/></td>
                                    <td className="px-4 py-2 text-right text-muted-foreground">{r.input_chars}/{r.output_chars}</td>
                                    <td className="px-4 py-2 text-right text-foreground">{(r.est_cost_inr || 0).toFixed(2)}</td>
                                </tr>
                            ))}
                            {audit.length === 0 && (
                                <tr><td colSpan={8} className="text-center text-muted-foreground py-8">No AI calls yet</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            {overrideOpen && <OverrideModal tenant={overrideOpen} onClose={() => { setOverrideOpen(null); load(); }}/>}
            {tier1Open && <Tier1Modal current={tiers.tier1} onClose={() => { setTier1Open(false); load(); }}/>}
        </div>
    );
}

function TierCard({ tier, num, onConfigure, onToggleGlobal }) {
    return (
        <div className="p-4 border border-border bg-background" data-testid={`tier-card-${num}`}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Tier {num}</div>
                <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-1.5 cursor-pointer" title="Global enable / disable">
                        <input
                            type="checkbox"
                            checked={!!tier.global_enabled}
                            onChange={e => onToggleGlobal(num, e.target.checked)}
                            className="accent-primary w-3.5 h-3.5"
                            data-testid={`tier-${num}-global-toggle`}
                        />
                        <span className={`text-[10px] uppercase tracking-[0.15em] ${tier.global_enabled ? "text-emerald-400" : "text-rose-400"}`}>
                            {tier.global_enabled ? "Enabled" : "Disabled"}
                        </span>
                    </label>
                    {onConfigure && (
                        <button onClick={onConfigure} data-testid={`tier-${num}-configure`} className="text-[10px] uppercase tracking-[0.15em] text-primary hover:underline">
                            Configure
                        </button>
                    )}
                </div>
            </div>
            <div className="text-sm text-foreground mt-1">{tier.label}</div>
            <div className="text-[11px] text-muted-foreground mt-2 space-y-0.5">
                {tier.provider !== undefined && <div>Provider: <span className="text-foreground">{tier.provider || "not set"}</span></div>}
                {tier.model !== undefined && <div>Model: <span className="text-foreground">{tier.model || "—"}</span></div>}
                <div>Margin: <span className="text-foreground">+{(tier.margin_pct ?? 0).toFixed(0)}%</span></div>
                {"loaded_amount" in tier && <div>Loaded: <span className="text-foreground">₹{(tier.loaded_amount ?? 0).toFixed(2)}</span></div>}
                {"has_key" in tier && (
                    <div>Status: {tier.has_key && tier.enabled ? <span className="text-emerald-400">Active</span> : <span className="text-amber-400">Not configured</span>}</div>
                )}
            </div>
        </div>
    );
}

function Tier1Modal({ current, onClose }) {
    const [form, setForm] = useState({
        provider: current.provider || "openai",
        model: current.model || "gpt-4o-mini",
        api_key: "",
        margin_pct: current.margin_pct || 30,
        loaded_amount: current.loaded_amount || 0,
        enabled: !!current.enabled,
    });
    const [busy, setBusy] = useState(false);
    const save = async () => {
        setBusy(true);
        try {
            const payload = { ...form };
            if (!form.api_key) delete payload.api_key; // keep existing
            await api.patch("/platform/ai-tiers/tier1", payload);
            toast.success("Tier 1 saved");
            onClose();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-background/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="tier1-modal">
            <div className="bg-card border border-border w-full max-w-lg p-6">
                <div className="overline">Tier 1 — Super Admin API</div>
                <h3 className="text-base text-foreground mt-1">Configure the platform-managed AI</h3>
                <p className="text-[11px] text-muted-foreground mt-1">Calls served from this key are billed to the tenant wallet at {form.margin_pct}% margin above the raw cost.</p>
                <div className="space-y-3 mt-5">
                    <Field label="Provider" testid="tier1-provider-field"><Select value={form.provider} onChange={v => setForm({ ...form, provider: v })} options={PROVIDERS} testid="tier1-provider"/></Field>
                    <Field label="Model" testid="tier1-model-field"><Input value={form.model} onChange={v => setForm({ ...form, model: v })} testid="tier1-model"/></Field>
                    <Field label="API key (leave blank to keep existing)" testid="tier1-api-key-field"><Input type="password" value={form.api_key} onChange={v => setForm({ ...form, api_key: v })} placeholder="sk-…" testid="tier1-api-key"/></Field>
                    <Field label="Margin (%)" testid="tier1-margin-field"><Input type="number" value={form.margin_pct} onChange={v => setForm({ ...form, margin_pct: parseFloat(v) || 0 })} testid="tier1-margin"/></Field>
                    <Field label="Loaded amount (₹) at provider — enter when you top up" testid="tier1-loaded-field"><Input type="number" value={form.loaded_amount} onChange={v => setForm({ ...form, loaded_amount: parseFloat(v) || 0 })} testid="tier1-loaded"/></Field>
                    <label className="flex items-center gap-2 text-xs text-foreground">
                        <input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} className="accent-primary" data-testid="tier1-enabled"/>
                        Enabled — when active, this tier takes priority over Emergent
                    </label>
                </div>
                <div className="mt-6 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 border border-border text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground">Cancel</button>
                    <button onClick={save} disabled={busy} data-testid="tier1-save" className="px-4 py-2 bg-primary text-primary-foreground hover:bg-[#C9A537] disabled:opacity-40 text-xs font-medium uppercase tracking-[0.15em] inline-flex items-center gap-2">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Save className="w-3.5 h-3.5"/>} Save
                    </button>
                </div>
            </div>
        </div>
    );
}

function SourceBadge({ source }) {
    const palette = {
        byo: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
        managed: "bg-amber-500/15 text-amber-300 border-amber-500/40",
        forced_managed: "bg-rose-500/15 text-rose-300 border-rose-500/40",
        forced_byo: "bg-blue-500/15 text-blue-300 border-blue-500/40",
        forced_override: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40",
    };
    return <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] border ${palette[source] || "bg-zinc-500/15 text-zinc-300 border-zinc-500/40"}`}>{(source || "—").replace(/_/g, " ")}</span>;
}

function Field({ label, children, testid }) {
    return (
        <div data-testid={testid}>
            <label className="overline">{label}</label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}

function Input({ value, onChange, testid, ...p }) {
    return <input value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid} {...p} className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm"/>;
}

function Select({ value, onChange, options, testid }) {
    return (
        <select value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid} className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm">
            {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
    );
}

function OverrideModal({ tenant, onClose }) {
    const [form, setForm] = useState({ ai_managed: false, ai_provider: "openai", ai_model: "gpt-4o-mini", ai_api_key: "" });
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            const payload = { tenant_id: tenant.tenant_id, ai_managed: form.ai_managed, ai_provider: form.ai_provider, ai_model: form.ai_model };
            if (form.ai_api_key) payload.ai_api_key = form.ai_api_key;
            await api.post("/platform/ai-overrides", payload);
            toast.success("Override saved");
            onClose();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };
    const clear = async () => {
        if (!window.confirm("Remove the super-admin pin for this tenant?")) return;
        setBusy(true);
        try {
            await api.delete(`/platform/ai-overrides/${tenant.tenant_id}`);
            toast.success("Override cleared");
            onClose();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-background/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="override-modal">
            <div className="bg-card border border-border w-full max-w-lg p-6">
                <div className="overline">Super-admin pin</div>
                <h3 className="text-base text-foreground mt-1">{tenant.tenant_name}</h3>
                <p className="text-[11px] text-muted-foreground mt-1">Force a specific provider + model on this firm. This overrides the firm's own AI settings AND the global policy.</p>

                <div className="space-y-3 mt-5">
                    <label className="flex items-center gap-2 text-xs text-foreground">
                        <input type="checkbox" checked={!!form.ai_managed} onChange={e => setForm({ ...form, ai_managed: e.target.checked })} className="accent-primary" data-testid="override-managed"/>
                        Force managed (Emergent Universal Key)
                    </label>
                    {!form.ai_managed && (
                        <>
                            <Field label="Provider"><Select value={form.ai_provider} onChange={v => setForm({ ...form, ai_provider: v })} options={PROVIDERS}/></Field>
                            <Field label="Model"><Input value={form.ai_model} onChange={v => setForm({ ...form, ai_model: v })}/></Field>
                            <Field label="API key (paste; stored encrypted)"><Input type="password" value={form.ai_api_key} onChange={v => setForm({ ...form, ai_api_key: v })} placeholder="sk-…"/></Field>
                        </>
                    )}
                </div>

                <div className="mt-6 flex items-center justify-between">
                    {tenant.has_super_override ? (
                        <button onClick={clear} data-testid="override-clear" className="text-xs uppercase tracking-[0.15em] text-rose-300 hover:text-rose-200 inline-flex items-center gap-1"><Trash2 className="w-3 h-3"/> Clear pin</button>
                    ) : <span/>}
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 border border-border text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground">Cancel</button>
                        <button onClick={save} disabled={busy} data-testid="override-save" className="px-4 py-2 bg-primary text-primary-foreground hover:bg-[#C9A537] disabled:opacity-40 text-xs font-medium uppercase tracking-[0.15em] inline-flex items-center gap-2">
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Save className="w-3.5 h-3.5"/>} Save pin
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
